/**
 * LogHoursDialog — 1-click quick entry for time on a Staycation segment.
 * Internal users: person picker (defaults self).
 * External users: person locked to self, segments filtered to assigned only.
 *
 * Props:
 *   open
 *   onClose
 *   defaultProjectId  — optional, pre-selects this segment
 *   restrictToProjectIds — optional array, limits dropdown (used by external mode)
 */
import { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Stack, IconButton, Typography, ListSubheader,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useQueryClient } from '@tanstack/react-query';
import { useProjectsTree, buildTree } from '../../hooks/useProjects';
import { useRoster } from '../../hooks/useRoster';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';
import { logAction } from '../../lib/actionLogger';

const today = () => new Date().toISOString().slice(0, 10);

export default function LogHoursDialog({ open, onClose, defaultProjectId, restrictToProjectIds }) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const { data: flat = [] } = useProjectsTree();
  const { data: roster = [] } = useRoster();

  const myRoster = roster.find((r) => r.profileId === profile?.id);
  const myName = myRoster?.name || profile?.name || '';
  const isExternal = !!profile?.is_external;

  // Segment options: kind=segment, ordered Season → Week → Segment
  const segments = useMemo(() => {
    const tree = buildTree(flat);
    const out = [];
    function walk(nodes, ancestors) {
      for (const n of nodes) {
        if (n.kind === 'segment') {
          const season = ancestors.find((a) => a.kind === 'season');
          const week = ancestors.find((a) => a.kind === 'episode');
          out.push({
            id: n.id,
            name: n.name,
            season: season?.name || '(no season)',
            week: week?.name || '',
          });
        }
        if (n.children?.length) walk(n.children, [...ancestors, n]);
      }
    }
    walk(tree, []);
    let filtered = out;
    if (Array.isArray(restrictToProjectIds) && restrictToProjectIds.length) {
      const allow = new Set(restrictToProjectIds);
      filtered = out.filter((s) => allow.has(s.id));
    }
    return filtered;
  }, [flat, restrictToProjectIds]);

  // Group for ListSubheader display
  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of segments) {
      const k = `${s.season} / ${s.week}`;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    }
    return Array.from(map.entries());
  }, [segments]);

  const [projectId, setProjectId] = useState(defaultProjectId || '');
  const [date, setDate] = useState(today());
  const [hours, setHours] = useState(1);
  const [notes, setNotes] = useState('');
  const [person, setPerson] = useState(myName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setProjectId(defaultProjectId || (segments[0]?.id || ''));
      setDate(today());
      setHours(1);
      setNotes('');
      setPerson(myName);
      setError(null);
    }
  }, [open, defaultProjectId, segments, myName]);

  const bump = (delta) => setHours((h) => Math.max(0, Math.round((Number(h) + delta) * 4) / 4));

  const save = async (addAnother = false) => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/time-entries', {
        project_id: projectId,
        person: isExternal ? myName : person,
        hours: Number(hours),
        date,
        notes,
        category: 'Production',
        kind: isExternal ? 'contractor' : 'crew',
      });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['time_entries'] });
      logAction({
        action: 'Create Time Entry',
        description: `Logged ${Number(hours)}h on a project — ${isExternal ? myName : person}`,
        details: { person: isExternal ? myName : person, hours: Number(hours), date, projectId, kind: isExternal ? 'contractor' : 'crew' },
      });
      if (addAnother) {
        setHours(1);
        setNotes('');
      } else {
        onClose();
      }
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Log hours</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isExternal && (
            <TextField
              select label="Person" value={person} onChange={(e) => setPerson(e.target.value)} fullWidth
            >
              {roster.map((r) => <MenuItem key={r.id} value={r.name}>{r.name}</MenuItem>)}
            </TextField>
          )}

          <TextField
            select label="Segment" value={projectId} onChange={(e) => setProjectId(e.target.value)} fullWidth
            disabled={segments.length === 0}
          >
            {segments.length === 0 && <MenuItem value="">No segments available</MenuItem>}
            {grouped.flatMap(([groupLabel, items]) => [
              <ListSubheader key={`h-${groupLabel}`}>{groupLabel}</ListSubheader>,
              ...items.map((s) => (
                <MenuItem key={s.id} value={s.id} sx={{ pl: 4 }}>{shortSegmentLabel(s.name)}</MenuItem>
              )),
            ])}
          </TextField>

          <TextField
            type="date" label="Date" value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small" onClick={() => bump(-0.25)}><RemoveIcon /></IconButton>
            <TextField
              type="number" label="Hours" value={hours}
              onChange={(e) => setHours(e.target.value)}
              inputProps={{ min: 0, step: 0.25 }}
              sx={{ flex: 1 }}
            />
            <IconButton size="small" onClick={() => bump(0.25)}><AddIcon /></IconButton>
          </Stack>

          <TextField
            label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
            fullWidth multiline rows={2}
          />

          {error && <Typography variant="body2" color="error">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => save(true)} disabled={busy || !projectId || !hours}>
          Save & add another
        </Button>
        <Button variant="contained" onClick={() => save(false)} disabled={busy || !projectId || !hours}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Show short suffix (e.g. "W1P2 Munaya Resort") instead of full em-dashed name
function shortSegmentLabel(name) {
  if (!name) return '';
  const idx = name.indexOf('\u2014'); // em-dash
  if (idx >= 0) return name.slice(idx + 1).trim();
  return name;
}
